import { type FunctionComponent, lazy, Suspense } from 'react'
import { Route, Routes, Outlet } from 'react-router'
import styled from 'styled-components'
import LoadingSpinner from './shared/components/LoadingSpinner/LoadingSpinner'
import { routing } from './routes'
import MarkdownPage from './pages/MarkdownPage'
import archeologyMd from './pages/static/UndergroundDossierPage/content/archeology.md'
import archiveMapHistoricMd from './pages/static/UndergroundDossierPage/content/archief-en-kaart-historische-onderzoeken-ondergrond.md'
import cablesPipesMd from './pages/static/UndergroundDossierPage/content/cables-pipes.md'
import climateMd from './pages/static/UndergroundDossierPage/content/climate.md'
import dikesMd from './pages/static/UndergroundDossierPage/content/dikes.md'
import energyMd from './pages/static/UndergroundDossierPage/content/energy.md'
import groundWaterMd from './pages/static/UndergroundDossierPage/content/ground-water.md'
import japaneseTreeMd from './pages/static/UndergroundDossierPage/content/japanese-tree.md'
import mapLayerInfoMd from './pages/static/map-layer-info.md'
import soilQualityMd from './pages/static/UndergroundDossierPage/content/environment-soil-quality.md'
import soilCompositionMd from './pages/static/UndergroundDossierPage/content/environment-soil-composition.md'
import treesGreensMd from './pages/static/UndergroundDossierPage/content/trees-greens.md'
import undergroundMobilityMd from './pages/static/UndergroundDossierPage/content/underground-mobility.md'
import undergroundObstaclesMd from './pages/static/UndergroundDossierPage/content/underground-obstacles.md'
import undergroundSpaceMd from './pages/static/UndergroundDossierPage/content/underground-space.md'
import amsterdamUndergrondMd from './pages/static/UndergroundDossierPage/content/amsterdam-underground.md'
import broPilot3dModelMd from './pages/static/UndergroundDossierPage/content/bro-pilot-3d-model-amstelstad.md'
import SidebarMapWrapper from './shared/components/SidebarMap/SidebarMapWrapper'
import MapProvider from './shared/contexts/map/MapProvider'
import LayerProvider from './shared/contexts/Layers/LayerProvider'
import Footer from './shared/components/Footer/Footer'
import { isFeatureEnabled, NEW_DATA_SELECTION, GLOSSARY } from './features'
import useParam from './shared/hooks/useParam'
import { polygonParam } from './pages/MapPage/query-params'
import { useHeaderSearch } from './shared/contexts/HeaderSearch/HeaderSearchContext'

const AboutPage = lazy(() => import(/* webpackChunkName: "AboutPage" */ './pages/static/AboutPage'))
const AboutWebsitePage = lazy(
  () => import(/* webpackChunkName: "AboutWebsitePage" */ './pages/static/AboutWebsitePage'),
)
const AccessibilityStatementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AccessibilityStatementPage" */ './pages/static/AccessibilityStatementPage'
    ),
)
const AddressDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "AddressDetail" */ './pages/OneDesignDetailPages/AddressDetail/AddressDetail'
    ),
)
const BouwblokDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "BouwblokDetail" */ './pages/OneDesignDetailPages/BouwblokDetail/BouwblokDetail'
    ),
)
const BuurtDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "BuurtDetail" */ './pages/OneDesignDetailPages/BuurtDetail/BuurtDetail'
    ),
)
const ComplexDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "ComplexDetail" */ './pages/OneDesignDetailPages/ComplexDetail/ComplexDetail'
    ),
)
const GebiedsGerichtWerkenDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "GebiedsGerichtWerkenDetail" */ './pages/OneDesignDetailPages/GebiedsGerichtWerkenDetail/GebiedsGerichtWerkenDetail'
    ),
)
const StedelijkGebiedProjectOfBelangenDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "StedelijkGebiedProjectOfBelangenDetail" */ './pages/OneDesignDetailPages/StedelijkGebiedProjectOfBelangenDetail/StedelijkGebiedProjectOfBelangenDetail'
    ),
)
const KadastraalObjectDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "KadastraalObjectDetail" */ './pages/OneDesignDetailPages/KadastraalObjectDetail/KadastraalObjectDetail'
    ),
)
const KadastraalSubjectDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "KadastraalSubjectDetail" */ './pages/OneDesignDetailPages/KadastraalSubjectDetail/KadastraalSubjectDetail'
    ),
)
const OpenbareRuimteDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "OpenbareRuimteDetail" */ './pages/OneDesignDetailPages/OpenbareRuimteDetail/OpenbareRuimteDetail'
    ),
)
const PandDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "PandDetail" */ './pages/OneDesignDetailPages/PandDetail/PandDetail'
    ),
)
const MaatschappelijkeActiviteitDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "MaatschappelijkeActiviteitDetail" */ './pages/OneDesignDetailPages/MaatschappelijkeActiviteitDetail/MADetail'
    ),
)
const MonumentDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "MonumentDetail" */ './pages/OneDesignDetailPages/MonumentDetail/MonumentDetail'
    ),
)
const StadsdeelDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "StadsdeelDetail" */ './pages/OneDesignDetailPages/StadsdeelDetail/StadsdeelDetail'
    ),
)
const VestigingDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "VestigingDetail" */ './pages/OneDesignDetailPages/VestigingDetail/VestigingDetail'
    ),
)
const WijkDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "WijkDetail" */ './pages/OneDesignDetailPages/WijkDetail/WijkDetail'
    ),
)
const WoonplaatsDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "WoonplaatsDetail" */ './pages/OneDesignDetailPages/WoonplaatsDetail/WoonplaatsDetail'
    ),
)
const DrawResultsDetailLegacy = lazy(
  () =>
    import(
      /* webpackChunkName: "DrawResultsDetailLegacy" */ './pages/OneDesignDetailPages/DrawResultsDetailLegacy/DrawResultsDetail'
    ),
)
const DrawResultsDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "DrawResultsDetail" */ './pages/OneDesignDetailPages/DrawResultsDetail/DrawResultsDetail'
    ),
)
const GeoSearchDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "GeoSearchDetail" */ './pages/OneDesignDetailPages/GeoSearchDetail/GeoSearchDetail'
    ),
)
const DataDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "DataDetail" */ './pages/OneDesignDetailPages/DataDetail/DataDetail'
    ),
)

const AnalyseSuiteDetailPage = lazy(
  () => import(/* webpackChunkName: "AnalyseSuiteDetailPage" */ './pages/AnalyseSuiteDetailPage'),
)
const CatalogDetailPage = lazy(
  () => import(/* webpackChunkName: "CatalogDetailPage" */ './pages/CatalogDetail/CatalogDetail'),
)
const CatalogOverviewPage = lazy(
  () =>
    import(/* webpackChunkName: "CatalogOverviewPage" */ './pages/CatalogOverview/CatalogOverview'),
)
const CompliancyAuditDetailPage = lazy(
  () =>
    import(/* webpackChunkName: "CompliancyAuditDetailPage" */ './pages/CompliancyAuditDetailPage'),
)
const ConstructionDossierListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ConstructionDossierListPage" */ './pages/ConstructionDossierListPage'
    ),
)
const ConstructionDossierPage = lazy(
  () => import(/* webpackChunkName: "ConstructionDossierPage" */ './pages/ConstructionDossierPage'),
)
const ContactPage = lazy(
  () => import(/* webpackChunkName: "ContactPage" */ './pages/static/ContactPage'),
)
const DataContactPage = lazy(
  () => import(/* webpackChunkName: "DataContactPage" */ './pages/static/DataContactPage'),
)
const DataPlatformServicesPage = lazy(
  () =>
    import(/* webpackChunkName: "DataPlatformServicesPage" */ './pages/DataPlatformServicesPage'),
)
const DataProcessingDetailPage = lazy(
  () =>
    import(/* webpackChunkName: "DataProcessingDetailPage" */ './pages/DataProcessingDetailPage'),
)
const DatasetCatalogDetailPage = lazy(
  () =>
    import(/* webpackChunkName: "DatasetCatalogDetailPage" */ './pages/DatasetCatalogDetailPage'),
)
const EmployeesPage = lazy(
  () => import(/* webpackChunkName: "EmployeesPage" */ './pages/static/EmployeesPage'),
)
const FAQPage = lazy(() => import(/* webpackChunkName: "FAQPage" */ './pages/static/FAQPage'))
const FeaturesPage = lazy(
  () => import(/* webpackChunkName: "FeaturesPage" */ './pages/static/FeaturesPage'),
)
const HelpPage = lazy(() => import(/* webpackChunkName: "HelpPage" */ './pages/static/HelpPage'))
const Homepage = lazy(() => import(/* webpackChunkName: "Homepage" */ './pages/Homepage'))
const InteractiveTablesDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "InteractiveTablesDetailPage" */ './pages/InteractiveTablesDetailPage'
    ),
)
const MapLayerDetailPage = lazy(
  () => import(/* webpackChunkName: "MapLayerDetailPage" */ './pages/MapLayerDetailPage'),
)
const NotFoundPage = lazy(
  () => import(/* webpackChunkName: "NotFoundPage" */ './pages/NotFoundPage'),
)
const QualityPage = lazy(
  () => import(/* webpackChunkName: "QualtiyPage" */ './pages/static/QualityPage'),
)
const SearchPage = lazy(() => import(/* webpackChunkName: "SearchPage" */ './pages/SearchPage'))
const ServicesPage = lazy(
  () => import(/* webpackChunkName: "ServicesPage" */ './pages/static/ServicesPage'),
)
const StandardisedInformationServicesDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StandardisedInformationServicesDetailPage" */ './pages/StandardisedInformationServicesDetailPage'
    ),
)
const TablesPage = lazy(
  () => import(/* webpackChunkName: "TablesPage" */ './pages/static/TablesPage'),
)
const TechnicalManagementPage = lazy(
  () =>
    import(
      /* webpackChunkName: "TechnicalManagementPage" */ './pages/static/TechnicalManagementPage'
    ),
)
const UndergroundDossierPage = lazy(
  () =>
    import(
      /* webpackChunkName: "UndergroundDossierPage" */ './pages/static/UndergroundDossierPage'
    ),
)
const DataSelection = lazy(
  () =>
    import(
      /* webpackChunkName: "DataSelection" */ './shared/components/DataSelection/DataSelection'
    ),
)
const GlossaryOverview = lazy(
  () =>
    import(/* webpackChunkName: "GlossaryOverview" */ './pages/GlossaryOverview/GlossaryOverview'),
)
const GlossaryDetail = lazy(
  () => import(/* webpackChunkName: "GlossaryDetail" */ './pages/GlossaryDetail/GlossaryDetail'),
)

const AppContainer = styled.main`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-top: 200px;
`

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.03);
`

export interface AppBodyProps {
  className: string
}

export const APP_CONTAINER_ID = 'main'

const AppBody: FunctionComponent<AppBodyProps> = ({ className }) => {
  const [geo] = useParam(polygonParam)
  const { backdrop } = useHeaderSearch()

  return (
    <AppContainer id={APP_CONTAINER_ID} className={`main-container ${className}`}>
      <Suspense fallback={<StyledLoadingSpinner />}>
        <Routes>
          <Route path={routing.constructionDossier.path} element={<ConstructionDossierPage />} />
          <Route
            element={
              <>
                <Outlet />
                <Footer />
              </>
            }
          >
            <Route path={routing.home.path} element={<Homepage />} />
            <Route path={routing.notFound.path} element={<NotFoundPage />} />
            <Route path={routing.help.path} element={<HelpPage />} />
            <Route path={routing.about.path} element={<AboutPage />} />
            <Route path={routing.aboutWebsite.path} element={<AboutWebsitePage />} />
            <Route path={routing.dataContact.path} element={<DataContactPage />} />
            <Route path={routing.contact.path} element={<ContactPage />} />
            <Route path={routing.services.path} element={<ServicesPage />} />
            <Route path={routing.technicalManagement.path} element={<TechnicalManagementPage />} />
            <Route path={routing.accessibility.path} element={<AccessibilityStatementPage />} />
            <Route path={routing.quality.path} element={<QualityPage />} />
            <Route path={routing.faq.path} element={<FAQPage />} />
            <Route path={routing.features.path} element={<FeaturesPage />} />
            <Route path={routing.tables.path} element={<TablesPage />} />
            <Route path={routing.employees.path} element={<EmployeesPage />} />
            <Route
              path={routing.undergroundMoblity.path}
              element={<MarkdownPage markdownFile={undergroundMobilityMd} />}
            />
            <Route
              path={routing.undergroundSpaceUtilization.path}
              element={<MarkdownPage markdownFile={undergroundSpaceMd} />}
            />
            <Route
              path={routing.undergroundObstacles.path}
              element={<MarkdownPage markdownFile={undergroundObstaclesMd} />}
            />
            <Route
              path={routing.undergroundJapaneseTree.path}
              element={<MarkdownPage markdownFile={japaneseTreeMd} />}
            />
            <Route
              path={routing.undergroundTreesGreens.path}
              element={<MarkdownPage markdownFile={treesGreensMd} />}
            />
            <Route
              path={routing.undergroundEnergy.path}
              element={<MarkdownPage markdownFile={energyMd} />}
            />
            <Route
              path={routing.undergroundCablesPipes.path}
              element={<MarkdownPage markdownFile={cablesPipesMd} />}
            />
            <Route
              path={routing.undergroundArcheology.path}
              element={<MarkdownPage markdownFile={archeologyMd} />}
            />
            <Route
              path={routing.undergroundClimate.path}
              element={<MarkdownPage markdownFile={climateMd} />}
            />
            <Route
              path={routing.undergroundDikes.path}
              element={<MarkdownPage markdownFile={dikesMd} />}
            />
            <Route
              path={routing.undergroundGroundwater.path}
              element={<MarkdownPage markdownFile={groundWaterMd} />}
            />
            <Route
              path={routing.undergroundSoilQuality.path}
              element={<MarkdownPage markdownFile={soilQualityMd} />}
            />
            <Route
              path={routing.undergroundSoilComposition.path}
              element={<MarkdownPage markdownFile={soilCompositionMd} />}
            />
            <Route
              path={routing.mapLayersInfo.path}
              element={<MarkdownPage markdownFile={mapLayerInfoMd} />}
            />
            <Route
              path={routing.amsterdamUnderground.path}
              element={<MarkdownPage markdownFile={amsterdamUndergrondMd} />}
            />
            <Route
              path={routing.archiveMapHistoric.path}
              element={<MarkdownPage markdownFile={archiveMapHistoricMd} />}
            />
            <Route
              path={routing.broPilot3dModel.path}
              element={<MarkdownPage markdownFile={broPilot3dModelMd} />}
            />
            <Route path={routing.undergroundDossier.path} element={<UndergroundDossierPage />} />
            <Route path={routing.search.path} element={<SearchPage />} />
            <Route
              path={routing.constructionDossierList.path}
              element={<ConstructionDossierListPage />}
            />
            <Route
              path={routing.dataPlatformServices.path}
              element={<DataPlatformServicesPage />}
            />
            <Route path={routing.mapLayerDetail.path} element={<MapLayerDetailPage />} />
            <Route
              path={routing.interactiveTablesDetail.path}
              element={<InteractiveTablesDetailPage />}
            />
            <Route
              path={routing.datasetCatalogDetail.path}
              element={<DatasetCatalogDetailPage />}
            />
            <Route
              path={routing.dataProcessingDetail.path}
              element={<DataProcessingDetailPage />}
            />
            <Route
              path={routing.compliancyAuditDetail.path}
              element={<CompliancyAuditDetailPage />}
            />
            <Route
              path={routing.standardisedInformationServicesDetail.path}
              element={<StandardisedInformationServicesDetailPage />}
            />
            <Route path={routing.analyseSuiteDetail.path} element={<AnalyseSuiteDetailPage />} />
            <Route path={routing.catalogOverview.path} element={<CatalogOverviewPage />} />
            <Route path={routing.catalogDetail.path} element={<CatalogDetailPage />} />
            {isFeatureEnabled(GLOSSARY) && (
              <>
                <Route path={routing.glossaryOverview.path} element={<GlossaryOverview />} />
                <Route path={routing.glossaryDetail.path} element={<GlossaryDetail />} />
              </>
            )}
          </Route>
          {/* OneDesign routes */}
          <Route
            element={
              <MapProvider>
                <LayerProvider>
                  <SidebarMapWrapper>
                    <Outlet />
                  </SidebarMapWrapper>
                </LayerProvider>
              </MapProvider>
            }
          >
            <Route path={routing.addressDetail.path} element={<AddressDetail />} />
            <Route path={routing.bouwblokDetail.path} element={<BouwblokDetail />} />
            <Route path={routing.buurtDetail.path} element={<BuurtDetail />} />
            <Route path={routing.complexDetail.path} element={<ComplexDetail />} />
            <Route
              path={routing.gebiedsGerichtWerkenDetail.path}
              element={<GebiedsGerichtWerkenDetail />}
            />
            <Route
              path={routing.stedelijkGebiedProjectOfBelangenDetail.path}
              element={<StedelijkGebiedProjectOfBelangenDetail />}
            />
            <Route path={routing.pandDetail.path} element={<PandDetail />} />
            <Route path={routing.monumentDetail.path} element={<MonumentDetail />} />
            <Route path={routing.maDetail.path} element={<MaatschappelijkeActiviteitDetail />} />

            <Route path={routing.woonplaatsDetail.path} element={<WoonplaatsDetail />} />
            <Route
              path={routing.kadastraalObjectDetail.path}
              element={<KadastraalObjectDetail />}
            />
            <Route
              path={routing.kadastraalSubjectDetail.path}
              element={<KadastraalSubjectDetail />}
            />
            <Route path={routing.openbareRuimteDetail.path} element={<OpenbareRuimteDetail />} />
            <Route path={routing.stadsdeelDetail.path} element={<StadsdeelDetail />} />
            <Route path={routing.vestigingDetail.path} element={<VestigingDetail />} />
            <Route path={routing.wijkDetail.path} element={<WijkDetail />} />
            <Route
              path={routing.addresses.path}
              element={
                isFeatureEnabled(NEW_DATA_SELECTION) && !geo ? (
                  <DataSelection />
                ) : (
                  <DrawResultsDetailLegacy />
                )
              }
            />
            <Route path={routing.establishments.path} element={<DrawResultsDetailLegacy />} />
            <Route path={routing.cadastralObjects.path} element={<DrawResultsDetailLegacy />} />
            <Route path={routing.drawTool.path} element={<DrawResultsDetail />} />
            <Route path={routing.dataSearchGeo.path} element={<GeoSearchDetail />} />
            <Route path={routing.dataDetail.path} element={<DataDetail />} />
          </Route>
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {backdrop && <Backdrop />}
    </AppContainer>
  )
}

export default AppBody
